import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, readoutStyle, titleStyle } from '../theme';
import { createButton } from '../ui/button';
import { createSettingsButton } from '../ui/settingsPanel';
import { maybeShowTutorial, createAdviserButton } from '../ui/adviserPanel';
import type { Campaign } from '@core/campaign/campaign';
import { RESEARCH_SHIP, SUBMARINE, type VesselId } from '@core/voyage/profiles';
import { saveCampaign } from '../saveGame';
import { audio } from '../audio/engine';
import type { WorldMapScene } from './WorldMapScene';

/**
 * Kopfzeile und Bedienleiste ueber dem Lagebild.
 *
 * Als eigene Szene aus demselben Grund wie `HudScene`: Objekte der Kartenszene
 * skalieren zwangslaeufig mit der Kamera, und die faehrt zwischen einfacher und
 * hundertfacher Vergroesserung. Text waere dort entweder unlesbar klein oder
 * bildschirmfuellend.
 *
 * Hier steht alles, was frueher die Stationsszene war — nur eben ueber der
 * Karte statt an ihrer Stelle.
 */
export class MapHudScene extends Phaser.Scene {
  private campaign!: Campaign;
  /** Das Fahrzeug, aus dessen Sicht die Lage gerechnet wird. */
  private viewpoint: VesselId = 'ship';

  constructor() {
    super('MapHud');
  }

  init(): void {
    this.viewpoint = 'ship';
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;

    // Voreingestellt ist das Fahrzeug, das draussen steht: Ueber das an der
    // Station gibt es nichts zu entscheiden, ueber das auf See schon.
    this.viewpoint = (this.scene.get('WorldMap') as WorldMapScene).focused;

    this.redraw();
  }

  /** Die Station ist der einzige sichere Punkt — nur dort wird gespeichert. */
  private get atBase(): boolean {
    return this.campaign.isAtBase(this.viewpoint);
  }

  /**
   * Zeichnet die Leiste neu.
   *
   * Oeffentlich, weil die Kartenszene sie anstoesst: Wer ein Fahrzeug
   * losschickt oder heimkommen laesst, aendert damit auch, was hier an
   * Schaltflaechen stehen darf.
   */
  refresh(): void {
    this.redraw();
  }

  private redraw(): void {
    this.children.removeAll();
    if (this.atBase) saveCampaign(this.campaign);

    const { width, height } = this.scale;
    const centreX = width / 2;

    this.renderHeader(centreX, width);
    this.renderActions(centreX, height - 34);

    maybeShowTutorial(this, this.campaign, 'calls');
  }

  private renderHeader(centreX: number, width: number): void {
    // Die Zeilen setzen aufeinander auf und die Leiste waechst mit ihnen —
    // sonst ragte die Fahrzeugzeile bei vergroesserter Schrift darunter heraus.
    const title = this.add
      .text(centreX, 12, t(this.atBase ? 'base.title' : 'base.title.atsea'), titleStyle(15))
      .setOrigin(0.5, 0);

    const clock = this.add
      .text(centreX, title.y + title.height + 6, this.clockLine(), readoutStyle(11, true))
      .setOrigin(0.5, 0);

    const vessel = this.renderVesselLine(centreX, clock.y + clock.height + 5);
    const barHeight = vessel.y + vessel.height + 10;

    const bar = this.add.rectangle(0, 0, width, barHeight, PALETTE.abyss, 0.72).setOrigin(0, 0);
    bar.setStrokeStyle(0);
    // Der Balken entsteht zuletzt und muss deshalb ausdruecklich nach hinten.
    bar.setDepth(-1);

    createSettingsButton(this, width - 16, title.y + title.height / 2, () => this.refreshAll());
    createAdviserButton(this, width - 16, clock.y + clock.height / 2, this.campaign, 'calls');
  }

  private clockLine(): string {
    const clock = this.campaign.clockHours;
    const day = Math.floor(clock / 24) + 1;
    const hour = Math.floor(clock % 24);
    // Das Ansehen steht nicht mehr allein da, sondern mit seiner Folge: wie
    // spaet ein Notruf hier ankommt. Eine Zahl ohne Wirkung ist Zierat.
    const delay = Math.round(this.campaign.reportDelay);
    const standing =
      delay <= 0
        ? t('base.reputation.instant', { value: this.campaign.reputation })
        : t('base.reputation.delayed', { value: this.campaign.reputation, hours: delay });

    return `${t('base.clock', { day, hour: String(hour).padStart(2, '0') })}  ·  ${standing}` +
      `  ·  ${t('base.act', { act: this.campaign.currentAct })}  ·  ${t('base.progress', {
        collected: this.campaign.cluesCollected,
        total: this.campaign.cluesPossible,
      })}`;
  }

  /**
   * Wo das Fahrzeug steht und wie es dasteht — anklickbar, um zwischen Schiff
   * und U-Boot zu wechseln. Auf See ist der Treibstoff die eigentliche
   * Nachricht: Es gibt keinen Nachschub, und die Strecke nach Hause muss
   * uebrig bleiben.
   */
  private renderVesselLine(centreX: number, y: number): Phaser.GameObjects.Text {
    const condition = this.campaign.vessels[this.viewpoint];
    const profile = this.viewpoint === 'ship' ? RESEARCH_SHIP : SUBMARINE;

    const text = [
      t(profile.nameKey),
      t('base.fuel', {
        used: Math.round(condition.fuel),
        available: Math.round(profile.fuelCapacity),
      }),
      t('base.hull', { value: Math.round(condition.hull) }),
      this.atBase
        ? t('base.at_station')
        : t('base.at_sea', { miles: Math.round(this.campaign.distanceHome(this.viewpoint)) }),
    ].join('  ·  ');

    const label = this.add
      .text(centreX, y, `${text}  ⇄`, readoutStyle(11, true))
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });

    label.on('pointerover', () => label.setColor(PALETTE.ink));
    label.on('pointerout', () => label.setColor(PALETTE.inkDim));
    label.on('pointerdown', () => {
      audio.cue('select');
      this.viewpoint = this.viewpoint === 'ship' ? 'submarine' : 'ship';
      // Karte und Leiste muessen dasselbe Fahrzeug meinen.
      (this.scene.get('WorldMap') as WorldMapScene).setFocus(this.viewpoint);
      this.redraw();
    });

    return label;
  }

  private renderActions(centreX: number, y: number): void {
    const buttons: { label: string; onClick: () => void }[] = [];

    /*
     * Wer faehrt, wartet nicht.
     *
     * Warten, Ankern, Tanken und Heimfahren setzen alle voraus, dass das
     * Fahrzeug steht. Waeren sie waehrend einer Fahrt anklickbar, koennte man
     * die Uhr zweimal gleichzeitig vorstellen — einmal ueber die Fahrt und
     * einmal ueber das Warten.
     */
    const underway = this.campaign.isUnderway(this.viewpoint);

    if (underway) {
      // Nichts als der Hinweis, wohin es geht — und die Beweislage.
    } else if (this.atBase) {
      buttons.push({
        label: t('base.wait'),
        onClick: () => {
          this.campaign.wait(12);
          this.refreshAll();
        },
      });

      const condition = this.campaign.vessels[this.viewpoint];
      const profile = this.viewpoint === 'ship' ? RESEARCH_SHIP : SUBMARINE;
      if (condition.fuel < profile.fuelCapacity - 0.5 || condition.hull < 99.5) {
        buttons.push({
          label: t('base.resupply'),
          onClick: () => {
            this.campaign.resupply(this.viewpoint);
            audio.cue('select');
            this.refreshAll();
          },
        });
      }
    } else {
      /*
       * Auf See vor Anker gehen.
       *
       * Nach einer Bergung stand bis M9 nur die Wahl zwischen sofort weiter
       * und der langen Heimfahrt. Dabei ist Liegenbleiben die naheliegendste
       * Entscheidung von allen: Man steht dort, wo etwas passiert ist, spart
       * die halbe Ueberfahrt zum naechsten Ruf — und verbraucht dabei keinen
       * Treibstoff, aber auch keine Werft in Reichweite.
       */
      buttons.push({
        label: t('base.anchor'),
        onClick: () => {
          this.campaign.wait(12);
          audio.cue('select');
          this.refreshAll();
        },
      });

      // Auf See gibt es nur einen Weg zurueck, und der kostet dieselbe Zeit
      // wie ein Einsatz.
      buttons.push({
        label: t('base.go_home'),
        onClick: () => {
          this.campaign.dispatch({
            vesselId: this.viewpoint,
            incident: null,
            speed: 'cruise',
            homebound: true,
          });
          audio.cue('select');
          this.refreshAll();
        },
      });
    }

    buttons.push({
      label: t('base.evidence', { count: this.campaign.ledger.size }),
      onClick: () => {
        this.scene.stop();
        this.scene.stop('WorldMap');
        this.scene.start('Evidence');
      },
    });

    if (this.campaign.isReadyForFinale) {
      buttons.push({
        label: t('base.finale'),
        onClick: () => {
          this.scene.stop();
          this.scene.stop('WorldMap');
          this.scene.start('Finale');
        },
      });
    }

    /*
     * Zweimal bauen: einmal ausserhalb des Bildes, um die tatsaechlichen
     * Breiten zu erfahren, dann endgueltig an der richtigen Stelle.
     *
     * Noetig, weil die Schaltflaechen seit der Schriftvergroesserung mit ihrer
     * Beschriftung wachsen. Mit einer festen Breite von 178 Pixeln standen sie
     * bei 130 Prozent Schrift entweder ineinander oder der Text ueber dem Rand.
     */
    const gap = 10;
    const widths = buttons.map((button) => {
      const probe = createButton(this, -9999, -9999, button.label, () => {}, {
        width: 150,
        height: 30,
        fontSize: 11,
      });
      const measured = probe.width;
      probe.destroy();
      return measured;
    });

    const total = widths.reduce((a, b) => a + b, 0) + (buttons.length - 1) * gap;
    let x = centreX - total / 2;

    buttons.forEach((button, index) => {
      const width = widths[index] as number;
      createButton(this, x + width / 2, y, button.label, button.onClick, {
        width: 150,
        height: 30,
        fontSize: 11,
      });
      x += width + gap;
    });
  }

  /**
   * Zeichnet Leiste und Karte neu.
   *
   * Nach jedem Schritt, der Zeit kostet: Notrufe koennen abgelaufen sein, neue
   * dazugekommen, und die Marker haengen an beidem.
   */
  private refreshAll(): void {
    (this.scene.get('WorldMap') as WorldMapScene).refreshIdle();
    this.redraw();
  }
}
